import { formatCurrency, getCurrencySymbol } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  LOCALE_ID,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { Checkout } from '@pdqdev/pdq-shared/dist/interfaces';
import { calculateGroupItemFees } from '@pdqdev/pdq-shared/dist/utils/fee-per-item-group';
import { GroupFeeCartLine } from '@pdqdev/pdq-shared/dist/utils/fee-per-item-group.types';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import {
  ItemFeePreviewService,
  PreviewRow as PreviewSourceRow,
} from '@pages/checkout-app/services/item-fee-preview.service';
import { CheckoutService } from '@pages/checkout-app/checkout.service';
import { toUnmatchedFee } from '../unmatched-fee.util';

// One row per rule, showing the first product that rule matched, then one row for the first
// product no rule matched. Each rule has at most one product here, so the rule's charge is that
// row's charge — see reconcile().
interface PreviewRow {
  key: string;
  lineId: string | null;
  label: string;
  matched: boolean;
  quantity: number;
  ruleLabel?: string;
  matchedRuleLabel: string;
  rateLabel?: string;
  feeCents?: number;
  // The rule charging this product, when it is not the rule the row stands for.
  shadowedByLabel?: string;
  unmatchedFeeCents?: number;
  unmatchedPercent?: number;
  excludedUnits?: number;
  excludedFeeCents?: number;
}

// Used only when a product matches several rules, where one rule's charge covers more than one
// row and so cannot be shown against any single one of them.
interface PreviewCharge {
  key: string;
  label: string;
  detail: string;
  feeCents: number;
  excludedFeeCents: number;
}

@Component({
  selector: 'item-fee-preview',
  templateUrl: './item-fee-preview.component.html',
  styleUrls: ['./item-fee-preview.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ItemFeePreviewComponent implements OnInit, OnDestroy {
  @Input() formGroup: UntypedFormGroup;

  rows: PreviewRow[] = [];
  charges: PreviewCharge[] = [];
  itemFeesCents = 0;
  loading = false;
  unavailable = false;
  currency = 'USD';

  private previewRows: PreviewSourceRow[] = [];
  private previewCart: GroupFeeCartLine[] = [];
  // Blank rules cannot be sent (the DTO refuses them), so the server's rule indexes are positions
  // in the filtered list. This maps them back to the rows on screen.
  private sentRuleIndexes: number[] = [];
  private quantities = new Map<string, number>();
  private readonly destroy$ = new Subject<boolean>();
  private readonly refresh$ = new Subject<void>();
  private refreshSubscription?: Subscription;
  private requestSeq = 0;

  constructor(
    private previewService: ItemFeePreviewService,
    private checkoutService: CheckoutService,
    private changeDetector: ChangeDetectorRef,
    @Inject(LOCALE_ID) private locale: string,
  ) {}

  ngOnInit(): void {
    this.checkoutService.shippingMethodCurrency$.pipe(takeUntil(this.destroy$)).subscribe((currency) => {
      this.currency = currency ?? 'USD';
      this.changeDetector.markForCheck();
    });

    this.refreshSubscription = this.refresh$
      .pipe(debounceTime(600), takeUntil(this.destroy$))
      .subscribe(() => this.loadProducts());

    this.groupRules.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((before, after) => this.matchSignature(before) === this.matchSignature(after)),
      )
      .subscribe(() => this.refresh$.next());

    this.formGroup
      .get('feePerItem')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.recalculate());

    // The rate sits outside feePerItem, so nothing above would repaint this view when it changes.
    this.formGroup
      .get('rate')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.changeDetector.markForCheck());

    if (this.groupRules?.length) {
      this.loadProducts();
    }
  }

  ngOnDestroy(): void {
    this.refreshSubscription?.unsubscribe();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  get groupRules(): UntypedFormArray {
    return this.formGroup.get('feePerItem.groupRules') as UntypedFormArray;
  }

  // Live-rate and tiered methods price the base elsewhere, so there is no rate to add here.
  // A fee can charge only the items no rule matches, and that deserves a preview too.
  private get chargesUnmatched(): boolean {
    return !!toUnmatchedFee(this.formGroup.get('feePerItem.unmatchedFee')?.value);
  }

  get baseRateKnown(): boolean {
    const rate = this.formGroup.get('rate')?.value;
    return rate !== null && rate !== undefined && `${rate}`.trim() !== '' && isFinite(Number(rate));
  }

  get baseRateCents(): number {
    return Math.round((Number(this.formGroup.get('rate')?.value) || 0) * 100);
  }

  get totalCents(): number {
    return this.baseRateCents + this.itemFeesCents;
  }

  setQuantity(lineId: string, quantity: number): void {
    this.quantities.set(lineId, Math.max(0, Math.floor(Number(quantity) || 0)));
    this.recalculate();
  }

  private async loadProducts(): Promise<void> {
    const seq = ++this.requestSeq;
    const all = (this.groupRules?.value ?? []) as Checkout.FeePerItemGroupRule[];
    const sent: number[] = [];
    const rules = all.filter((rule, index) => {
      if (!`${rule?.value ?? ''}`.trim()) {
        return false;
      }
      sent.push(index);
      return true;
    });
    if (!rules.length && !this.chargesUnmatched) {
      this.previewRows = [];
      this.previewCart = [];
      this.rows = [];
      this.charges = [];
      this.itemFeesCents = 0;
      this.loading = false;
      this.unavailable = false;
      this.changeDetector.markForCheck();
      return;
    }

    this.loading = true;
    this.unavailable = false;
    try {
      const result = await this.previewService.preview({
        rules,
        overlapMode: this.formGroup.get('feePerItem.overlapMode')?.value,
      });
      if (seq !== this.requestSeq) {
        return;
      }
      this.unavailable = !result.available;
      this.sentRuleIndexes = sent;
      this.previewRows = result.rows;
      this.previewCart = result.cart;
      this.seedQuantities();
      this.recalculate();
    } catch {
      if (seq !== this.requestSeq) {
        return;
      }
      // Must never block the settings screen.
      this.previewRows = [];
      this.previewCart = [];
      this.rows = [];
      this.charges = [];
      this.unavailable = true;
      this.changeDetector.markForCheck();
    } finally {
      if (seq === this.requestSeq) {
        this.loading = false;
        this.changeDetector.markForCheck();
      }
    }
  }

  // A preview line names a rule by its position in what was sent, not on screen.
  private ruleAt(sentIndex: number | null): Checkout.FeePerItemGroupRule | null {
    if (sentIndex === null || sentIndex === undefined) {
      return null;
    }
    const onScreen = this.sentRuleIndexes[sentIndex];
    return onScreen === undefined ? null : (this.groupRules.at(onScreen)?.value ?? null);
  }

  private seedQuantities(): void {
    this.previewRows.forEach((row) => {
      if (!row.lineId || this.quantities.has(row.lineId)) {
        return;
      }
      const rule = this.ruleAt(row.ruleIndex);
      this.quantities.set(row.lineId, Math.max(1, Number(rule?.firstNFree ?? 0) + 1));
    });
  }

  private recalculate(): void {
    const lines = this.previewCart.map((line) => ({
      ...line,
      quantity: this.quantities.get(line.lineId) ?? 0,
    }));

    const result = calculateGroupItemFees({
      lines,
      rules: this.groupRules?.value ?? [],
      overlapMode: this.formGroup.get('feePerItem.overlapMode')?.value ?? Checkout.RuleOverlapMode.FIRST,
      unmatchedFee: toUnmatchedFee(this.formGroup.get('feePerItem.unmatchedFee')?.value) ?? undefined,
    });

    this.itemFeesCents = result.totalFeeCents;
    this.rows = this.previewRows.map((row) => this.toRow(row, result));
    // The rows carry the amounts whenever they account for the whole fee; the per-rule list is the
    // fallback for the case where one rule's charge spans more than one row.
    this.charges = this.rowsAccountForTotal() ? [] : this.toCharges(result);
    if (this.charges.length) {
      this.rows = this.rows.map((row) => ({ ...row, feeCents: undefined }));
    }
    this.changeDetector.markForCheck();
  }

  private rowsAccountForTotal(): boolean {
    const priced = this.rows.reduce((total, row) => total + (row.feeCents ?? 0), 0);
    return priced === this.itemFeesCents;
  }

  private toCharges(result: ReturnType<typeof calculateGroupItemFees>): PreviewCharge[] {
    const charges: PreviewCharge[] = result.rules
      .filter((charge) => charge.matchedUnits > 0)
      .map((charge) => ({
        key: `rule-${charge.ruleIndex}`,
        label: `${this.groupRules.at(charge.ruleIndex)?.value?.value ?? 'Rule'}`,
        detail: charge.freeUnits
          ? `${charge.matchedUnits} matched, ${charge.freeUnits} excluded`
          : `${charge.matchedUnits} matched`,
        feeCents: charge.feeCents,
        excludedFeeCents: charge.excludedFeeCents ?? 0,
      }));

    if (result.unmatchedUnits > 0) {
      charges.push({
        key: 'unmatched',
        label: 'Items in no group',
        detail: `${result.unmatchedUnits} matched no rule`,
        feeCents: result.unmatchedFeeCents,
        excludedFeeCents: 0,
      });
    }
    return charges;
  }

  private toRow(source: PreviewSourceRow, result: ReturnType<typeof calculateGroupItemFees>): PreviewRow {
    const rule = this.ruleAt(source.ruleIndex);
    // A product can appear on two rows, so the row's own identity is the rule it stands for.
    const key = source.ruleIndex === null ? 'unmatched' : `rule-${source.ruleIndex}`;
    const onScreenRule = source.ruleIndex === null ? undefined : this.sentRuleIndexes[source.ruleIndex];
    const ruleLabel = onScreenRule === undefined ? undefined : `Rule ${onScreenRule + 1}`;

    if (!source.lineId) {
      return {
        key,
        lineId: null,
        label: rule?.value ? `${rule.value} item` : 'Items in no group',
        matched: false,
        quantity: 0,
        ruleLabel,
        matchedRuleLabel: 'No products match this rule yet.',
      };
    }

    const breakdown = result.lines.find((entry) => entry.lineId === source.lineId);
    const matchedIndexes = breakdown?.matchedRuleIndexes ?? [];
    const charged = matchedIndexes.map((index) => result.rules[index]).filter(Boolean);

    return {
      key,
      lineId: source.lineId,
      label: source.title ?? 'Product',
      matched: true,
      quantity: this.quantities.get(source.lineId) ?? 0,
      ruleLabel,
      // The first matching rule is the one the shopper is billed under, which is not necessarily
      // the rule this product was sampled for.
      ...(matchedIndexes.length
        ? {
            feeCents: charged.reduce((total, charge) => total + charge.feeCents, 0),
            rateLabel: ItemFeePreviewComponent.rateLabel(this.groupRules.at(matchedIndexes[0])?.value),
          }
        : { feeCents: result.unmatchedFeeCents }),
      matchedRuleLabel: matchedIndexes.length
        ? `Matched: ${matchedIndexes.map((index) => this.groupRules.at(index)?.value?.value).join(', ')}`
        : 'No rule matched',
      ...(source.chargedByRuleIndex === null || source.chargedByRuleIndex === undefined
        ? {}
        : { shadowedByLabel: this.ruleAt(source.chargedByRuleIndex)?.value ?? 'an earlier rule' }),
      ...ItemFeePreviewComponent.excludedForLine(result, matchedIndexes, source.lineId),
      ...(matchedIndexes.length ? {} : this.unmatchedNote()),
    };
  }

  excludedLabel(row: PreviewRow): string {
    const head = `${row.excludedUnits} of ${row.quantity} excluded (cheapest match`;
    if (!row.excludedFeeCents) {
      return `${head})`;
    }
    // The code is what tells Angular how many decimals this currency has; without it every
    // amount here would print two, and JPY would read differently from the pipes around it.
    const saved = formatCurrency(
      row.excludedFeeCents / 100,
      this.locale,
      getCurrencySymbol(this.currency, 'wide'),
      this.currency,
    );
    return `${head}, \u2212${saved})`;
  }

  private static excludedForLine(
    result: ReturnType<typeof calculateGroupItemFees>,
    matchedIndexes: number[],
    lineId: string,
  ): { excludedUnits?: number; excludedFeeCents?: number } {
    let excludedUnits = 0;
    let excludedFeeCents = 0;
    let exact = true;
    for (const index of matchedIndexes) {
      const charge = result.rules[index];
      if (!charge?.freeUnitsByLine?.[lineId]) {
        continue;
      }
      excludedUnits += charge.freeUnitsByLine[lineId];
      // The saving follows the price or weight of the freed units, not their count, and free
      // units go cheapest-first. Split across lines, no per-line figure is derivable here.
      if (charge.freeUnitsByLine[lineId] !== charge.freeUnits) {
        exact = false;
        continue;
      }
      excludedFeeCents += charge.excludedFeeCents ?? 0;
    }
    if (!excludedUnits) {
      return {};
    }
    return exact ? { excludedUnits, excludedFeeCents } : { excludedUnits };
  }

  private static rateLabel(rule: Checkout.FeePerItemGroupRule | undefined): string | undefined {
    if (!rule) {
      return undefined;
    }
    const amount = Number(rule.amount ?? 0);
    if (rule.type === Checkout.GroupFeeType.PERCENT_OF_ITEM_PRICE) {
      return `${amount}% of item price`;
    }
    const step = Number(rule.step ?? 1);
    const unit = rule.stepUnit === Checkout.FeeUnitType.POUNDS ? 'lb' : 'item';
    const money = `$${amount.toFixed(2)}`;
    return step > 1 ? `${money} per ${step} ${unit}s` : `${money} per ${unit}`;
  }

  private unmatchedNote(): Pick<PreviewRow, 'unmatchedFeeCents' | 'unmatchedPercent'> {
    const fee = toUnmatchedFee(this.formGroup.get('feePerItem.unmatchedFee')?.value);
    if (!fee?.amount) {
      return {};
    }
    return fee.type === Checkout.GroupFeeType.PERCENT_OF_ITEM_PRICE
      ? { unmatchedPercent: Number(fee.amount) }
      : { unmatchedFeeCents: Math.round(Number(fee.amount) * 100) };
  }

  private matchSignature(rules: Checkout.FeePerItemGroupRule[] = []): string {
    return (rules ?? []).map((rule) => `${rule?.matchBy}|${rule?.operator}|${rule?.value}`).join('~');
  }
}
